// Cartão "Áreas e CNAE" da Visão geral do contrato (04/10/2026).
// Pedido do Lucas: "na página do contrato precisa constar o quanto está
// associado a cada área". O quanto sai das PARCELAS (valor × setor) — a mesma
// conta da receita por setor da DRE; a divisão gravada no contrato é o padrão
// das parcelas novas. Num contrato principal, as parcelas dos aditivos entram.
import { useEffect, useState } from 'react';
import { Alert, Button, Spin, Tag, Tooltip, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { Contrato } from './types';
import { fmt } from './utils';
import { SETORES } from '../../utils/areasEmpresa';
import { DetalheAreas, PENDENCIAS, CONFIANCA, rotuloOrigem, descreverRateio } from './areasContrato';
import { carregarAreasDoContrato, mensagemDeErro } from './apiAreas';
import { ModalAreasContrato } from './ModalAreasContrato';

const pct = (p: number) => (Math.round(p * 1000) / 10).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + '%';

export function CardAreasCnae({ contrato, onAtualizar }: { contrato: Contrato; onAtualizar: () => void }) {
  const [det, setDet] = useState<DetalheAreas | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<{ msg: string; semAcesso: boolean } | null>(null);
  const [editando, setEditando] = useState(false);

  async function carregar() {
    setCarregando(true); setErro(null);
    try {
      setDet(await carregarAreasDoContrato(contrato.id));
    } catch (e: any) {
      // Falha de leitura NÃO vira "sem áreas": fica o erro, com o botão de tentar.
      setDet(null);
      setErro({ msg: mensagemDeErro(e), semAcesso: e?.response?.status === 403 });
    } finally { setCarregando(false); }
  }
  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrato.id]);

  if (carregando && !det) return <div style={{ textAlign: 'center', padding: 18 }}><Spin size="small" /></div>;
  if (erro && !det) {
    return erro.semAcesso
      ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>Seu perfil não vê as áreas e o CNAE do contrato.</Typography.Text>
      : <Alert type="error" showIcon message="Não consegui carregar as áreas" description={erro.msg}
          action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />;
  }
  if (!det) return null;

  const temParcelas = det.numeros.parcelas > 0;
  // A barra e a lista são SÓ das parcelas. A divisão gravada no contrato (o
  // padrão das parcelas novas) aparece uma vez, na linha "Parcela nova nasce
  // com" — usá-la também na barra fazia um contrato com parcelas todas sem
  // setor mostrar "Topografia 100%" e "Sem setor 100%" juntos.
  const linhas = det.por_area.map(a => ({ chave: a.setor, rotulo: a.rotulo, cor: SETORES[a.setor]?.cor || '#888', proporcao: a.proporcao, valor: a.valor }));
  const semSetor = det.valores?.sem_setor || 0;
  const propSemSetor = det.valores && det.valores.total > 0 ? semSetor / det.valores.total : 0;
  const sugD = det.sugestao.divisao, sugC = det.sugestao.cnae;
  const etiquetas = [...det.pendencias, ...det.avisos].filter(p =>
    p === 'parcelas_sem_cnae' || p === 'cnae_divergente' || p === 'setor_divergente' || (p === 'parcelas_sem_setor' && !(semSetor > 0)));

  return (
    <div>
      {(linhas.length > 0 || propSemSetor > 0) && (
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#eee', marginBottom: 10 }}>
          {linhas.map(l => <div key={l.chave} title={`${l.rotulo}: ${pct(l.proporcao)}`} style={{ background: l.cor, width: `${l.proporcao * 100}%` }} />)}
          {propSemSetor > 0 && <div title={`Sem setor: ${pct(propSemSetor)}`} style={{ background: '#d9d9d9', width: `${propSemSetor * 100}%` }} />}
        </div>
      )}
      {linhas.map(l => (
        <div key={l.chave} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '2px 0' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: l.cor, flexShrink: 0 }} />
          <span style={{ flex: 1, color: '#333' }}>{l.rotulo}</span>
          <span style={{ fontWeight: 600, minWidth: 48, textAlign: 'right' }}>{pct(l.proporcao)}</span>
          {l.valor != null && (
            <span style={{ color: '#666', minWidth: 104, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(l.valor)}</span>
          )}
        </div>
      ))}
      {semSetor > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '2px 0', color: '#B91626' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: '#d9d9d9', flexShrink: 0 }} />
          <span style={{ flex: 1, fontWeight: 600 }}>Sem setor ({det.numeros.sem_setor_n} parcela{det.numeros.sem_setor_n === 1 ? '' : 's'})</span>
          <span style={{ fontWeight: 600, minWidth: 48, textAlign: 'right' }}>{pct(propSemSetor)}</span>
          <span style={{ minWidth: 104, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(semSetor)}</span>
        </div>
      )}
      {!linhas.length && !semSetor && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {temParcelas ? 'As parcelas ainda não têm setor.' : 'Ainda não há parcelas neste contrato.'}
        </Typography.Text>
      )}
      {(det.por_area.length > 0 || semSetor > 0) && (
        <div style={{ fontSize: 10.5, color: '#999', marginTop: 4 }}>
          Pelas parcelas do contrato{det.contrato.aditivos ? ` e dos ${det.contrato.aditivos} aditivo(s)` : ''} — a mesma conta da receita por setor da DRE.
        </div>
      )}

      {det.subareas.some(s => s.subarea) && (
        <div style={{ fontSize: 11.5, color: '#555', marginTop: 8 }}>
          <b>Sub-áreas (pela venda):</b>{' '}
          {det.subareas.filter(s => s.subarea).map(s => `${s.rotulo} › ${s.subarea}${s.valor != null ? ` ${fmt(s.valor)}` : ''}`).join(' · ')}
        </div>
      )}

      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #f0f0f0', fontSize: 12, lineHeight: 1.7 }}>
        <div>
          <Typography.Text type="secondary">Parcela nova nasce com: </Typography.Text>
          {det.divisao.rateio
            ? <><b>{descreverRateio(det.divisao.rateio)}</b>{' '}
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  ({det.divisao.herdada ? `segue o contrato principal ${det.contrato.pai_numero || ''}` : `origem: ${rotuloOrigem(det.divisao.origem)}`})
                </Typography.Text></>
            : <Tag color="red">divisão por área não definida</Tag>}
        </div>
        <div>
          <Typography.Text type="secondary">CNAE do contrato: </Typography.Text>
          {det.cnae.codigo
            ? <><b>{det.cnae.codigo}</b>{det.cnae.descricao ? ` — ${det.cnae.descricao}` : ''}{' '}
                {det.cnae.herdado && <Tag style={{ fontSize: 10 }}>do contrato principal</Tag>}
                {det.cnae.manual && <Tag style={{ fontSize: 10 }}>escolhido à mão</Tag>}</>
            : <Tag color="orange">não definido</Tag>}
        </div>
        {!det.divisao.rateio && sugD && (
          <div style={{ color: '#666' }}>
            Sugestão de divisão <Tag color={CONFIANCA[sugD.confianca].cor} style={{ marginInline: 2 }}>{CONFIANCA[sugD.confianca].rotulo}</Tag>
            {descreverRateio(sugD.rateio)} ({rotuloOrigem(sugD.origem)})
          </div>
        )}
        {!det.cnae.codigo && sugC && (
          <div style={{ color: '#666' }}>
            Sugestão de CNAE <Tag color={CONFIANCA[sugC.confianca].cor} style={{ marginInline: 2 }}>{CONFIANCA[sugC.confianca].rotulo}</Tag>
            {sugC.codigo} ({rotuloOrigem(sugC.origem)})
          </div>
        )}
      </div>

      {/* Etiqueta só para o que o cartão ainda não disse: "sem divisão" e "sem
          CNAE" já estão nas duas linhas acima, e a parcela sem setor já tem a
          linha vermelha com o valor (quando o perfil vê valores). */}
      {etiquetas.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {etiquetas.map(p => {
            const cfg = PENDENCIAS[p];
            const quantos = p === 'parcelas_sem_cnae' ? det.numeros.abertas_sem_cnae_n
              : p === 'cnae_divergente' ? det.numeros.cnae_divergente_n
              : p === 'parcelas_sem_setor' ? det.numeros.sem_setor_n : 0;
            return (
              <Tooltip key={p} title={cfg?.dica}>
                <Tag color={cfg?.cor || 'default'} style={{ margin: 0 }}>{cfg?.rotulo || p}{quantos ? ` (${quantos})` : ''}</Tag>
              </Tooltip>
            );
          })}
        </div>
      )}

      {det.pode_editar && (
        <div style={{ marginTop: 10 }}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(true)}>
            {det.pendencias.length ? 'Arrumar áreas e CNAE' : 'Editar'}
          </Button>
        </div>
      )}

      <ModalAreasContrato
        contratoId={contrato.id} aberto={editando}
        onFechar={() => setEditando(false)}
        onSalvo={(d) => { setEditando(false); setDet(d); onAtualizar(); }} />
    </div>
  );
}
